import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n, getLocaleFromUrl} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";
import {isCrawler} from "../../utils/crawler";

import {Dialog} from "../flipp-dialog/Dialog";
import {Link} from "../flipp-link/Link";
import {OldBrowserContainer} from "../flipp-old-browser-container/OldBrowserContainer";
import {WebGLContainer} from "../flipp-webgl-container/WebGLContainer";
import {ApplicationTopBar} from "../flipp-application-topbar/ApplicationTopBar";
import {CircularDestinationHeader} from "../flipp-circular-destination-header/CircularDestinationHeader";
import {AppRating} from "../flipp-app-rating/AppRating";
import { DesktopBanner } from "../flipp-desktop-banner/DesktopBanner";

import {ClippingType, Clipping, Category} from "../../definitions/CommonDefinitions";
import {ClippedCoupon} from "../../definitions/CouponDefinitions";
import {inject} from "../../services/di";
import {LocationService, PostalCode, Country} from "../../services/LocationService";
import {BlacklistService} from "../../services/BlacklistService";
import {FlyerService} from "../../services/FlyerService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {ClippingService} from "../../services/ClippingService";
import {AuthenticationService} from "../../services/AuthenticationService";
import {AuthStatus} from "../../services/definitions/IAuthenticationService";
import {CouponService} from "../../services/CouponService";
import {LinkService} from "../../services/LinkService";
import {ConfigService, RuntimeConfig, Experience, DistributionMode} from "../../services/ConfigService";
import {UserAgentService} from "../../services/UserAgentService";
import {StorageService} from "../../services/StorageService";
import {AccountsService} from "../../services/AccountsService";
import {CrossBrowseAction} from "../../services/definitions/IAnalyticsService";
import {SeoService} from "../../services/SeoService";
import BreadcrumbsService, { IBreadcrumb } from "../../services/BreadcrumbsService";
import { MobileBanner } from "../flipp-mobile-banner/MobileBanner";
import { MobilePopupBanner } from "../flipp-mobile-popup-banner/MobilePopupBanner";
import { FlippCitiesFooterLinks } from "../flipp-cities-footer-links/FlippCitiesFooterLinks";
import MetadataService from "../../services/MetadataService";
import {SeoDataService} from "../../services/SeoDataService";
import {DataFormService} from "../../services/DataFormService";
import { BlackFridayService } from "../../services/BlackFridayService";

export type PageContext = "shoppingList" | "default";

interface PageProperties extends JSX.HTMLProps {
	show: "flyers" | "coupons";
	withSidebar?: string;
	search?: string;
	context?: PageContext;
	merchant?: string;
}

type PageModel = [Category[], IBreadcrumb[]];
@element("flipp-page")
export class Page extends Component<PageProperties, PageModel> {
	refs: {
		locationDialog: Dialog,
		anchor: HTMLButtonElement,
		postalCodeInput: HTMLInputElement,
		publisherName: HTMLInputElement,
		content: HTMLElement,
		sidebarDialog: Dialog,
		emailConfirm: Dialog;
		sidebarLinks: HTMLElement;
		languageButton: HTMLElement;
	};

	@inject location: LocationService;
	@inject storage: StorageService;
	@inject couponService: CouponService;
	@inject flyerService: FlyerService;
	@inject userAgent: UserAgentService;
	@inject accounts: AccountsService;
	@inject auth: AuthenticationService;
	@inject config: ConfigService;
	@inject analytics: AnalyticsService;
	@inject blacklist: BlacklistService;
	@inject seo: SeoService;
	@inject breadcrumbsService: BreadcrumbsService;
	@inject linkService: LinkService;
	@inject metadataService: MetadataService;
	@inject seoData: SeoDataService;
	@inject dataFormService: DataFormService;
	@inject blackFridayService: BlackFridayService;

	@attr show: string;
	@attr search: string;
	@attr context: PageContext;
	@attr withSidebar: string = "true";
	@attr showCouponsCategories: string = "true";
	@attr merchant: string;

	isModalClosed = true;
	appBannerClosed = false;

	onAttach() {
		window.addEventListener("resize", this._handleResize);
		window.addEventListener("flipp-auth", this._handleAuth);
		// need to allow for space for other child elements, so applying class on the top level
		this.className = this.config.CDP_SIMPLE_HEADER ? "circular-destination-mode" : "";
		window.addEventListener("unsubscribe-from-redirect", this.dataFormService.unsubscribeFromRedirect);
		window.addEventListener("mobile-banner-closed", this.resizeButton);
	}

	onDetach() {
		window.removeEventListener("resize", this._handleResize);
		window.removeEventListener("flipp-auth", this._handleAuth);
		window.removeEventListener("unsubscribe-from-redirect", this.dataFormService.unsubscribeFromRedirect);
		window.removeEventListener("mobile-banner-closed", this.resizeButton);
	}

	get scrollTop() {
		return this.refs.content.scrollTop;
	}

	set scrollTop(top: number) {
		this.refs.content.scrollTop = top;
	}

	prepare() {
		return Promise.all([
			isCrawler() ? null : this.show === "coupons" ?
				this.couponService.getCouponCategories() :
				this.flyerService.getCategories(),
			this.getBreadcrumbs(),
		]);
	}

	renderLoading() {
		return this._doRender(true, [], []);
	}

	render([categories, breadcrumbs]: PageModel) {
		return this._doRender(false, categories, breadcrumbs);
	}

	checkMobileBanner() {
		const languageButton = this.refs.languageButton;
		const sidebarLinks = this.refs.sidebarLinks;
		if (!this.appBannerClosed && languageButton) {
			languageButton.classList.add("higher-button");
		} else if (!this.appBannerClosed && sidebarLinks) {
			sidebarLinks.classList.add("higher-links");
		}
	}

	resizeButton() {
		document.querySelector(".button-area").classList.remove("higher-button");
		document.querySelector(".sidebar-links").classList.remove("higher-links");
	}

	getBreadcrumbs(): Promise<IBreadcrumb[]> {
		return isCrawler() ? this.breadcrumbsService.getBreadcrumbs() : Promise.resolve([]);
	}

	renderBreadcrumbs(breadcrumbs: IBreadcrumb[]) {
		return <ol class="breadcrumbs">
			{breadcrumbs.map((breadcrumb) => (
				<li>
					<a href={breadcrumb.path}>
						{breadcrumb.label}
					</a>
				</li>
			))}
		</ol>;
	}

	getMerchantLink() {
		return isCrawler() ? <a href={this.linkService.getMerchantHref(this.merchant)}>{this.merchant} flyers coupons and deals</a> : null;
	}

	getTopMerchants() {
		const merchants = getLocaleFromUrl() === "en-us" ? this.seoData.getTopUsMerchants() : this.seoData.getTopCanadaMerchants();
		return merchants.slice(20);
	}

	getLocaleFromUrl() {
		const locale = window.location.pathname.split("/")[1];
		if (this.seo.isLocale(locale)) {
			return locale;
		}

		return this.metadataService.getLocale;
	}

	renderMerchantsLinks() {
		if (!isCrawler()) {
			return null;
		}

		const links = this.getTopMerchants().map((merchant: string) => {
			return <li><a href={this.linkService.getMerchantHref(merchant, this.getLocaleFromUrl())}>{merchant}</a></li>;
		});
		return <div><ul>{links}</ul></div>;
	}

	getFooter() {
		return (
			<footer>
				{this.renderMerchantsLinks()}
				<FlippCitiesFooterLinks />
			</footer>
		);
	}

	checkAppBanner = (whenWasHidden: any) => {
		if (whenWasHidden) {
			this.appBannerClosed = true;
		}
	}

	private handleAppLinkClick_ = (e: Event) => {
		if (this.userAgent.android || this.userAgent.iOS) {
			this.analytics.trackMobileInstall(this.userAgent.android ? "android" : "ios");
		}
	}

	private renderCategories = (mobile: boolean, categories: Category[]) => {
		return categories && categories.map((category) =>
				<Link to={category}>
					<span flex-grow>{i18n(category.name)}</span>
					<span class="pill">{category.count}</span>
				</Link>
			);
	}

	private renderFlyersCategories = (mobile: boolean, categories: Category[]) => {
		const appLink = this.userAgent.getAppLinkHref("Application Top Bar");
		const helpLink = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
			"https://flipp.freshdesk.com/support/home" :
			i18n("help_link");

		if (mobile) {
			return (
				<div>
					{this.renderCategories(mobile, categories.slice(0, 6))}
					<hr/>
					<div ref="sidebarLinks" class="sidebar-links">
						<a target="_blank" href="https://corp.flipp.com">{i18n("company")}</a>
						<a target="_blank" href="https://corp.flipp.com/about">{i18n("about_us")}</a>
						<a target="_blank" href="https://corp.flipp.com/careers">{i18n("careers")}</a>
						<a target="_blank" href={appLink} onClick={(e: Event) => {
							this.handleAppLinkClick_(e);
						}}>{i18n("mobile_app")}</a>
						<a target="_blank" href={helpLink}>{i18n("help")}</a>
						<hr/>
						<a target="_blank" href="https://corp.flipp.com/flipp/terms_of_use">{i18n("terms_of_use")}</a>
						<a target="_blank" href="https://corp.flipp.com/flipp/privacy">{i18n("privacy_policy")}</a>
						<button class="side-bar-btn" onClick={this.dataFormService.handleFormClicked}>{i18n("my_data")}</button>
						<a target="_blank" href="https://corp.flipp.com/patents">{i18n("patents")}</a>
						<a target="_blank" href="https://corp.flipp.com/privacy#INFO55">{i18n("ad_choices")}</a>
						<a href="https://www.flippappinions.com/c/a/6kiwEIx1Ce287lz8RyHYr8" target="blank">
							<button class="side-bar-btn-feedback">
								<img src="https://f.wishabi.net/creative/Distribution/images/icons/myMessageSvg.svg" width="44px" height="22px" alt=""/>
								<span>{i18n("give_feedback")}</span></button>
						</a>
						<hr/>
					</div>
				</div>

			);
		} else {
			return this.renderCategories(mobile, categories);
		}
	}

	private _renderNavigation(loading: boolean, categories: Category[]) {
		const changeLocation = i18n("change_location");
		const flyersForPostalCode = i18n("flyers_for_postal_code", {
			postalCode: this.location.postalCode
		});
		const canada = this.location.country === Country.CANADA;
		const french = this.location.acceptsLocale("fr");
		const analyticParams = this.analytics.analyticsParameters;

		return	(
			<Dialog ref="sidebarDialog"
							class="sidebar-dialog"
							anchor-point="left top">
				<nav flex-vertical flex-noshrink>
					<div>
						<Link to="listing" class="logo" title="Flipp">
							{icons.flippLogo}
						</Link>
					</div>
					{this.userAgent.mobile
						?
						<div class="get-app-block">
							<div class="get-app-stars">
								<AppRating />
							</div>
							<div class="get-app-button">
								<a href="https://flipp.app.link/YPAcWzwUy7">
									<button>{i18n("get_app")}</button>
								</a>
							</div>
						</div>
						: null
					}

					<div class="sidebar" flex-grow flex-vertical>
						{isCrawler() ? null : (
							<button class="location" ref="anchor"
									anchor-point="left bottom"
									onClick={this._handleLocationButtonClick_}>
								{icons.locationMarker}
								<div>
									{flyersForPostalCode}<br/>
									<span>({changeLocation})</span>
								</div>
							</button>
						)}

						<Dialog anchor-point="left top" popover
								ref="locationDialog"
								class="location-dialog">
							<div class="arrow"></div>
							<form class="postal-code"
									method="get"
									action={LinkService.getInstance().linkTo(this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
										"distribution" : "redirect")}
									onSubmit={this._handlePostalCodeSubmit}>
								<input type="hidden" name="page" value="listing" />
								<input type="hidden" name="locale" value={this.location.locale} />
								<input type="hidden" name="old_fsa" value={this.config.RUNTIME === RuntimeConfig.DEFAULT ?
									this.location.postalCode : null} />
								<input type="hidden" name="location_source" value="1" /> {/* 1 is 'USER_SUPPLIED' */}
								<input type="hidden" name="blacklist_merchant_ids" value={this.blacklist.getBlacklist().join(",")} />
								{this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
									Object.getOwnPropertyNames(analyticParams).map((prop) =>
										<input type="hidden" name={prop} value={analyticParams[prop]} />
									)
									:
									null
								}
								{ this.config.CDP_SIMPLE_HEADER ?
									<div>
										<input ref="publisherName" type="hidden" name="publisher_name" value={this.config.CDP_PUBLISHER_NAME} />
										<input type="hidden" name="website_url" value={this.config.CDP_BACK_LINK} />
									</div>
									:
									null
								}
								{
									(!isCrawler()) ?
										<div>
											<p>{i18n("enter_your_postal_code")}</p>
											<input type="text" name="postal_code"
												placeholder={i18n("postal_code")}
												value={this.location.postalCode}
												maxLength={7}
												ref="postalCodeInput"/>
											<button type="submit" class="find-flyers">{i18n("find_flyers")}</button>
										</div> : null
								}
								</form>
						</Dialog>
						{this.userAgent.mobile ?
							<Link to="shopping_list">
								<div class="myCustomElement">
									<img class="myPageSl" src="https://f.wishabi.net/creative/Distribution/images/icons/myPageSl.svg" alt=""/>
								</div>
								<span>{i18n("shopping_list")}</span>
								{/* <ClippingsPill /> */}
							</Link>
							:
							null
						}
						{this.userAgent.mobile ?
							<Link to="favourites">
								<div class="myCustomElement">
									<img class="myPageFavourites" src="https://f.wishabi.net/creative/Distribution/images/icons/myPageFavourites.svg" alt=""/>
								</div>
								<span>{i18n("fav_page")}</span>
							</Link>
							:
							null
						}

						{(!this.userAgent.mobile && !isCrawler()) ?
							<Link to="favourites">
								<div class="myCustomElement">
									<img class="myPageFavourites" src="https://f.wishabi.net/creative/Distribution/images/icons/myPageFavourites.svg" alt=""/>
								</div>
								<span>{i18n("fav_page")}</span>
							</Link>
							:
							null
						}
						{!isCrawler() ?
							<Link to="listing">
								<div class="myCustomElement">
									<img class="myPageFlyers" src="https://f.wishabi.net/creative/Distribution/images/icons/myPageFlyers.svg" alt=""/>
								</div>
								<span>{i18n("flyers")}</span>
							</Link>
							:
							null
						}
				{(this.userAgent.mobile && this.config.RUNTIME === RuntimeConfig.DISTRIBUTION) || isCrawler()
							? null
							: <Link id="coupons-link"
									to="coupons">
								<div class="myCustomElement">
									<img class="myPageCoupons" src="https://f.wishabi.net/creative/Distribution/images/icons/myPageCoupons.svg" alt=""/>
								</div>
							<span>{i18n("coupons")}</span>
						</Link>}


						{loading ?
							<div flex-grow flex-vertical>
								<hr />
								<div class="categories" flex-grow>
									<div fill-absolute scrollable>
										<h2>{this.show === "coupons" ? i18n("coupons") : i18n("flyers")}</h2>
										<div class="categories" flex-grow flex-vertical>
											<div class="loading">
												<div class="spinner"></div>
											</div>
										</div>
									</div>
								</div>
							</div>
							: this.show === "coupons" ?
								<div flex-grow flex-vertical>
									<hr />
									<div class="categories" flex-grow>
										<div fill-absolute scrollable>
											<h2>{i18n("coupons")}</h2>
											{ categories.map((category) =>
												<Link to={category}>
													<span flex-grow>{i18n(category.name)}</span>
													<span class="pill">{category.count}</span>
												</Link>
											)}
										</div>
									</div>
								</div>
								:
								<div flex-grow flex-vertical>
									<hr />
									<div class="categories" flex-grow>
										<div fill-absolute scrollable>
											<h2>{i18n("flyers")}</h2>
											{this.renderFlyersCategories(this.userAgent.mobile, categories)}
										</div>
									</div>
								</div>
						}

						{isCrawler() ? null : canada && french ?
							<div ref="languageButton" class="button-area" flex-vertical>
								{ this.config.CDP_SIMPLE_HEADER ?
									<a class="lang"
										href={LinkService.getInstance().linkTo("distribution", {
											publisher_name: this.config.CDP_PUBLISHER_NAME,
											website_url: this.config.CDP_BACK_LINK,
											channel_id: analyticParams["channel_id"],
											locale: "en-ca",
											postal_code: this.location.postalCode,
											traffic_source: "destination_simple"
										})}>
										<span>English</span>
									</a>
									:
									<a class="lang"
										onClick={() => this.switchLang("en-ca")}
									>
										<span>English</span>
									</a>
								}
							</div>
							: canada && !french ?
								<div ref="languageButton" class="button-area" flex-vertical>
									{ this.config.CDP_SIMPLE_HEADER ?
										<a class="lang"
											onClick={this._setLocale.bind(this, "fr-ca")}
											href={LinkService.getInstance().linkTo("distribution", {
												publisher_name: this.config.CDP_PUBLISHER_NAME,
												website_url: this.config.CDP_BACK_LINK,
												channel_id: analyticParams["channel_id"],
												locale: "fr-ca",
												postal_code: this.location.postalCode,
												traffic_source: "destination_simple"
											})}>
											<span>Français</span>
										</a>
										:
										<a class="lang"
											onClick={() => this.switchLang("fr-ca")}
										>
											<span>Français</span>
										</a>
									}
								</div>
								:
								null
						}
					</div>
				</nav>
			</Dialog>
		);
	}

	private switchLang = (lang: string) => {
		this._setLocale(lang);
		if (!this.blackFridayService.isOnHost()) {
			window.location.href = lang === "fr-ca" ? "/circulaires" : "/flyers";
		} else {
			window.location.reload();
		}
	}

	private checkModal = () => {
		const value = this.storage.getItem("redirected_modal_closed");
		if (value) {
			this.isModalClosed = JSON.parse(value).closed;
		}
	}

	private _closeDiscoverSlModal = (e: Event) => {
		if (e) {
			// If clicked on the close X, then prevent the <Link to=sl /> from kicking into gear.
			// ----------------------------------------------------------------------
			e.preventDefault();
		}

		this.isModalClosed = true;
		this.storage.setItem("redirected_modal_closed", JSON.stringify({"closed": true}));
		const el = document.querySelector(".discover-sl-wrapper");
		if (el) {
			el.outerHTML = "";
		}
	}

	private _persistHidingDiscoverSlModal = (e: Event) => {
		this.isModalClosed = true;
		this.storage.setItem("redirected_modal_closed", JSON.stringify({"closed": true}));
		const el = document.querySelector(".discover-sl-wrapper");
		if (el) {
			el.outerHTML = "";
		}
	}

	private _doRender(loading: boolean, categories: Category[], breadcrumbs: IBreadcrumb[]) {
		this.checkModal();
		const modalMessage = i18n(this.storage.getItem("message_key"));
		if (!loading) {
			setTimeout(() => this.checkMobileBanner(), 1000);
		}
		if (!loading && document.querySelector(".discover-sl-wrapper")) {
			setTimeout(() => {
				this.storage.removeItem("message_key");
			}, 0);
		}

		if (!loading && isCrawler()) {
			this.seo.setCrawlerCss();
		}

		const redirectModal = (
			<a class="discover-sl-wrapper" onClick={this._persistHidingDiscoverSlModal}>
				<div class="discover-sl" flex-horizontal>
					<button class="close" onClick={this._closeDiscoverSlModal}>{icons.x}</button>
					<div flex-horizontal>
						<div>{icons.myDiscoverSl}</div>
						<div class="discover-msg">
							<p>{modalMessage}</p>
						</div>
					</div>
				</div>
			</a>);

		const french = this.location.acceptsLocale("fr");
		const circularDestinationContainer = this.config.CDP_SIMPLE_HEADER ? <CircularDestinationHeader /> : null;
		const withSidebar = () => this.withSidebar === "true";

		return <template>

			<DesktopBanner />
			{isCrawler() ? this.renderBreadcrumbs(breadcrumbs) : null}
			{(isCrawler() && this.merchant) ? this.getMerchantLink() : null}
			{circularDestinationContainer}
			{withSidebar() ? this._renderNavigation(loading, categories) : null}
			<div class="application" flex-grow flex-vertical>
				<MobileBanner checkAppBanner={this.checkAppBanner}/>
				{ (this.config.showAppTopBar && !isCrawler()) ?
					<ApplicationTopBar search={this.search} context={this.context}>
						{this.config.EXPERIENCE === Experience.DEFAULT ?
							<button class="hamburger"
									anchor-point="left top"
									title={i18n("open_navigation_menu")}
									aria-label={i18n("open_navigation_menu")}
									onClick={this._handleHamburgerClick}>
								<div>{icons.hamburger}</div>
								<div class="flipp-logo-wrapper">{icons.flippLogo}</div>
							</button>
							: this.config.EXPERIENCE === Experience.DISTRIBUTION ?
										this.config.aidHero ?
												<div class="home-link" style={{backgroundColor: this.config.getThemeColor()}}>
													<div class="flipp-logo-wrapper">{icons.flippLogo}</div>
												</div>
												:
												<Link class="home-link"
															to="listing"
															onClick={this._handleCrossBrowse}
															style={{backgroundColor: this.config.getThemeColor()}}>
													<div class="flipp-logo-wrapper">{icons.flippLogo}</div>
												</Link>
								:
								null
						}
					</ApplicationTopBar>
					:
					null
				}
				{(!this.isModalClosed && modalMessage) ? redirectModal : null}
				<main ref="content" onScroll={this._handleScroll} flex-grow>
					<content />
				</main>
				{!isCrawler() ?
					<Dialog class="email-confirm" ref="emailConfirm">
						<div class="content-wrapper" flex-vertical>
							<h2>{i18n("email_signup")}</h2>

							<img defer src="https://f.wishabi.net/creative/Distribution/images/email_notices.png" alt=""/>

							<p>
								{i18n("email_signup_prop")}<a target="_blank" href="https://corp.flipp.com/legal/terms_of_use/">{i18n("email_signup_terms")}</a>
								{i18n("email_signup_prop_2")}<a target="_blank" href="https://corp.flipp.com/legal/privacy/">{i18n("email_signup_privacy")}</a> or
								<a target="_blank" href="https://corp.flipp.com/contact-us/">{i18n("email_signup_contact")}</a>
							</p>
							<div class="button-area" flex-horizontal>
								<button class="decline" type="button"
										onClick={this.close_}>{i18n("email_decline")}</button>
								<button class="accept" type="button"
										onClick={this.acceptCASL_}>{i18n("email_accept")}</button>
							</div>

							<p class="address">Flipp Corporation, 12-3250 Bloor St W, East Tower, Etobicoke, ON, M8X
								2X9, Canada</p>
						</div>
					</Dialog>
					: null
				}

			</div>

			<MobilePopupBanner />
			{!isCrawler() ? null :
				<div flex-grow flex-vertical>
					<hr />
					<div class="categories" flex-grow>
						<div fill-absolute scrollable>
							<h2>{i18n("coupons")}</h2>
							{ categories && categories.map((category) =>
								<Link to={category}>
									<span flex-grow>{i18n(category.name)}</span>
									<span class="pill">{category.count}</span>
								</Link>
							)}
						</div>
					</div>
				</div>
			}

			{!isCrawler() ?
				<span>
					<OldBrowserContainer />
					<WebGLContainer />
				</span>
			:
				null
			}
			{this.getFooter()}
		</template>;
	}

	private _handleScroll = (e: Event) => {
		this.dispatchEvent(new Event("scroll"));
	}

	private _handleLocationButtonClick_ = (e: Event) => {
		this.refs.locationDialog.showModal(this.refs.anchor);
	}

	private _handlePostalCodeSubmit = (e: Event) => {
		const input = this.refs.postalCodeInput;
		// clean up
		input.value = input.value.replace(" ", "");

		if (!PostalCode.isValid(input.value)) {
			input.setAttribute("invalid", "");

			e.preventDefault();
			return;
		}

		const currentCode = new PostalCode(this.location.postalCode);
		const newCode = new PostalCode(input.value);
		const hasChangedCountry = currentCode.country !== newCode.country;

		if (this.config.RUNTIME === RuntimeConfig.DISTRIBUTION) {
			/* Prevent users from changing to a different country FSA in the distribution experience */
			if (hasChangedCountry) {
				input.setAttribute("invalid", "");
				e.preventDefault();
				return;
			}
		} else if (hasChangedCountry) {
			const { value } = (e.target as any).locale;
			if (this.location.isUsLocale(value)) {
				(e.target as any).locale.value = "en-ca";
			} else if (this.location.isCanadianLocale(value)) {
				(e.target as any).locale.value = "en-us";
			}
		}

		const postalCode = new PostalCode(input.value);
		this.storage.removeItem("locale");
		this.location.savePostalCode(postalCode);

		// Manually clean publisher name values since form will replace spaces with '+'
		if (this.refs.publisherName) {
			const publisherNameVal = this.refs.publisherName.value;
			this.refs.publisherName.value = publisherNameVal.replace(/\s{1}/g, "__");
		}
	}

	private _handleHamburgerClick = (e: Event) => {
		this.refs.sidebarDialog.showModal(e.currentTarget as Element);
	}

	private _handleResize = (e: Event) => {
		if (!this.userAgent.mobile) {
			if (this.refs.locationDialog.open) this.refs.locationDialog.close();
			if (this.refs.sidebarDialog.open) this.refs.sidebarDialog.close();
		}
	}

	private _setLocale = (lang: string) => {
		if (this.config.DISTRIBUTION_MODE === DistributionMode.SIMPLE || this.config.DISTRIBUTION_MODE === DistributionMode.INLINE) {
			const CDPCookieJSON = Cookies.getJSON("cdp-parameters");
			CDPCookieJSON["locale"] = lang;
			Cookies.set("cdp-parameters", JSON.stringify(CDPCookieJSON), {expires: this.config.CDP_COOKIE_EXPIRY});
		}
		else {
			this.storage.setItem("locale", lang);
		}
	}

	private _handleAuth = (e: CustomEvent) => {
		if (e.detail.action === "login") {
			this.showCasl_();
		}
	}

	private showCasl_ = () => {
		const user = this.auth.user.token;
		if (user.email_subscribed === null) {
			if (this.location.country === Country.CANADA) {
				this.refs.emailConfirm.showModal();
				this.storage.setItem("email_confirmed",
					JSON.stringify({"confirmed": true}));
			} else {
				this.accounts.updateSubscription(user, true);
			}
		}
	}

	private close_ = (evt?: Event) => {
		this.refs.emailConfirm.close();
		this.accounts.updateSubscription(this.auth.user.token, false);
	}

	private acceptCASL_ = (evt: Event) => {
		this.refs.emailConfirm.close();
		this.accounts.updateSubscription(this.auth.user.token, true);
	}

	private isEmailConfirmed = () => {
		const value = this.storage.getItem("email_confirmed");
		if (value) {
			const data = JSON.parse(value);
			return data.confirmed;
		}
		return false;
	}

	private _handleCrossBrowse = () => {
		this.analytics.trackCrossBrowse(CrossBrowseAction.TopLogo);
	}
}

// --- flipp-clippings-pill ------------------------------------>

@element("flipp-clippings-pill")
export class ClippingsPill extends Component<{}, number> {

	@inject clippingService: ClippingService;
	@inject auth: AuthenticationService;
	@inject couponsService: CouponService;

	onAttach() {
		window.addEventListener("flipp-clippingschanged",
				this._handleClippingsChange);
		window.addEventListener("flipp-auth", this._handleAuthChange);
	}

	onDetach() {
		window.removeEventListener("flipp-clippingschanged",
				this._handleClippingsChange);
		window.removeEventListener("flipp-auth", this._handleAuthChange);
	}

	prepare() {
		let clippedCouponsFn = Promise.resolve<ClippedCoupon[]>([]);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			clippedCouponsFn = this.couponsService.getClippedCoupons(this.auth.user);
		}
		return Promise.all([
			this.couponsService.getCoupons(),
			clippedCouponsFn,
			this.clippingService.getClippings()
		]).then(([coupons, clippedCoupons, clippings]) => {
			/* Create a map of coupon ids for easy lookup */
			const couponIds: {[key: number]: boolean} = {};
			coupons.forEach((coupon) => couponIds[coupon.id] = true);

			const prunedClippings: Clipping[] = [];
			clippings.forEach((clipping) => {
				/* Don't show print coupons that are not available in the current locale */
				if (clipping.type === ClippingType.PRINT_COUPON && couponIds[Number(clipping.item.id)]) {
					prunedClippings.push(clipping);
				} else if (clipping.type !== ClippingType.PRINT_COUPON) {
					prunedClippings.push(clipping);
				}
			});
			return clippedCoupons.length + prunedClippings.length;
		});
	}

	render(count: number) {
		return <span class="pill">{count}</span>;
	}

	private _handleClippingsChange = (e: Event) => {
		this.invalidate();
	}

	private _handleAuthChange = (e: Event) => {
		this.invalidate();
	}

}
